import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild
} from '@angular/core'
import * as d3 from 'd3'
import { tip as d3tip } from 'd3-v6-tip'

import { AggregationDataset } from '../../../shared/interfaces/visualizations/aggregation-dataset.interface'
import { SmartLinkService } from '../../../shared/services/smart-link.service'

declare var require: any

@Component({
  selector: 'app-sunburst',
  templateUrl: './sunburst.component.html',
  styleUrls: ['./sunburst.component.scss']
})
export class SunburstComponent implements OnChanges, AfterViewInit {
  @ViewChild('sunburstChart') chartContainer: ElementRef

  @Input() dataset: AggregationDataset

  height: number = 500

  private margin: any = { top: 20, bottom: 20, left: 20, right: 20 }
  private width: number

  private tau: number = 2 * Math.PI
  private totalCount = 0
  private minAngleToDisplayLabelInside = 0.3
  private startAngle: number
  private endAngle: number
  private innerRadius: number
  private middleRadius: number
  private outerRadius: number

  private topTip: any
  private bottomTip: any

  isChartVisible: boolean = false

  static formatData(data: any[]) {
    data.forEach((mainItem, index) => {
      mainItem.count = 0
      ;(mainItem.subItems || []).forEach((subItem: any, subIndex: any) => {
        // We count total incentives to calculate radiuses.
        mainItem.count += subItem.count
        // Store parent index in sub item for color coherence.
        subItem.parentIndex = index
        subItem.indexInParent = subIndex
      })
    })
    return data
  }

  constructor(private smartLinkService: SmartLinkService) {}

  ngAfterViewInit(): void {
    this.buildSunburstChart()
    this.isChartVisible = true
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isChartVisible) {
      this.buildSunburstChart()
    }
  }

  buildSunburstChart(): void {
    const mainThemes = SunburstComponent.formatData(this.dataset.data)

    let subThemes: any[] = []
    this.totalCount = 0
    for (const mainTheme of mainThemes) {
      subThemes = subThemes.concat(mainTheme.subItems)
      this.totalCount += mainTheme.count
    }

    const element = this.chartContainer.nativeElement
    this.width = element.offsetWidth - this.margin.left - this.margin.right
    this.innerRadius = (this.height - this.margin.top - this.margin.bottom) / 8
    this.middleRadius = this.innerRadius * 2
    this.outerRadius = this.innerRadius * 4
    element.innerHTML = ''

    const colors = [
      '#c3cbcb',
      '#d5464b',
      '#529fd2',
      '#99c7d6',
      '#A089B4',
      '#AD71A2',
      '#C14B86',
      '#F15D89',
      '#D4266B',
      '#DF566C',
      '#EA876D',
      '#F1A76E',
      '#F8C76E',
      '#FFE76F',
      '#E7EB79',
      '#D6EB79',
      '#44ad9d',
      '#aa89f4',
      '#5dabcc',
      '#ff7877',
      '#f7cb7f',
      '#54beac',
      '#75d0e8',
      '#6bb9d9',
      '#ff8d8c',
      '#f9c072',
      '#5dc7b3',
      '#b091f4',
      '#7ac9e7',
      '#ff9897',
      '#f7cb7f',
      '#65d0ba',
      '#b599f5',
      '#89d8f4',
      '#ffa2a2',
      '#f6d187',
      '#6dd9c2',
      '#baa1f5',
      '#61c9db',
      '#ffacac',
      '#f5d68e',
      '#75e2c9',
      '#c0a8f5',
      '#4dc1cd',
      '#f1615d',
      '#fdcdac',
      '#bff29b',
      '#c5b0f5',
      '#39b9be',
      '#e5605a',
      '#ffc79c',
      '#adeb98',
      '#cbb8f5',
      '#26b0ae',
      '#f1615d',
      '#ffc18c',
      '#9be496',
      '#d0c0f5',
      '#12a89d',
      '#ffbb7b',
      '#88dd94',
      '#009f8b',
      '#e5605a',
      '#ffab45',
      '#307fa5',
      '#c46363',
      '#b9d1a8',
      '#c987c4',
      '#fdae61',
      '#3ca496',
      '#DDB9A1',
      '#FCC951',
      '#ABD9E9',
      '#8C759F',
      '#aa89f4',
      '#fd6161',
      '#ea94b6',
      '#75e2c9',
      '#ffa62e',
      '#258cbc',
      '#fcdb51',
      '#aa89f4',
      '#89d8f4',
      '#eaa77b',
      '#E569A7',
      '#ADF58E',
      '#80955D',
      '#D5CA9D',
      '#B3FCE0',
      '#FAA35C',
      '#E5C4DC',
      '#F5D68E',
      '#D9DCCA',
      '#009f8b',
      '#77EAA5',
      '#FF9100',
      '#81ACA0',
      '#A8FF72',
      '#D9CCBF',
      '#1ADBBE',
      '#E8BE15',
      '#36BB8F',
      '#E96B39',
      '#61E8CA',
      '#66FF8E',
      '#6CC15F',
      '#D9CD00',
      '#D0ECFF',
      '#F7EEE7',
      '#00B6BF',
      '#FFCD86',
      '#D9CCBF',
      '#81ACA0',
      '#E59026',
      '#f9f4f4',
      '#f9f4f6',
      '#f4f9f8',
      '#f4f8f9',
      '#f9f8f4',
      '#f6f4f9',
      '#f4f7f9',
      '#f9f6f4',
      '#136E59',
      '#FD8F8F',
      '#DDAC6C',
      '#D1C592',
      '#FF69A4',
      '#7D43FF',
      '#97B9C9',
      '#27B7EA',
      '#FFA050',
      '#54DE7F',
      '#A8CAC2',
      '#FEC7C7',
      '#EED5B5',
      '#E8E2C8',
      '#FEB3D1',
      '#BDA0FD',
      '#CBDCE4',
      '#92DBF4',
      '#FFCFA7',
      '#C8EDD9',
      '#B3E2CD',
      '#E7FCEC',
      '#CEF9B8',
      '#A6E9FF',
      '#FEF79F',
      '#FFD5A9',
      '#A8ED90',
      '#7AD6FF',
      '#FEF16F',
      '#FFB77E',
      '#83DB70',
      '#59C3FF',
      '#FDEA4C',
      '#FF9B5D',
      '#51C444',
      '#23A3FF',
      '#FCE111',
      '#FF6C28',
      '#239185'
    ]

    // Create arcs
    const arc = d3
      .arc()
      .innerRadius(this.innerRadius)
      .outerRadius(this.middleRadius)
    const outerArc = d3
      .arc()
      .innerRadius(this.middleRadius)
      .outerRadius(this.outerRadius)

    // create svg const
    const svg = d3
      .select(element)
      .append('svg')
      .attr('width', this.width)
      .attr('height', this.height)

    // Tooltips
    this.topTip = d3tip()
      .attr('class', 'is-tooltip is-tooltip-dark is-small is-bottom')
      .offset([0, 0])
      .html(
        (e: any, d: any) =>
          `<p class="mb-0  has-text-weight-bold">${d.label}</p> <p class="my-0">Incentives : ${d.count}</p>`
      )
    this.bottomTip = d3tip()
      .attr('class', 'is-tooltip is-tooltip-dark is-small is-bottom')
      .offset([160, 0])
      .html(
        (e: any, d: any) =>
          `<p class="mb-0  has-text-weight-bold">${d.label}</p> <p class="my-0">Incentives : ${d.count}</p>`
      )
    svg.call(this.topTip)
    svg.call(this.bottomTip)

    const g = svg
      .append('g')
      .attr(
        'transform',
        'translate(' + this.width / 2 + ',' + this.height / 2 + ')'
      )
      .on('mouseout', (d: any) => {
        centralText.text('')
        this.topTip.hide()
        this.bottomTip.hide()
      })

    // Inner circle
    const slice = g
      .selectAll('.slice')
      .data(mainThemes)
      .enter()
      .append('g')
      .attr('class', 'slice pointer')

    slice
      .append('path')
      .datum((d: any) => this.getAngles(d, subThemes.length))
      .style('fill', (d: any, i: any) => colors[i])
      .style('stroke', '#fff')
      .attr('d', arc as any)
      .on('mouseover', (e: any, d: any) => {
        centralText.text(`${d.count} incentive${d.count > 1 ? 's' : ''}`)
        const angle = ((d.startAngle + d.endAngle) / 2 / this.tau) * 360
        if (angle > 90 && angle <= 270) {
          this.bottomTip.show(e, d)
        } else {
          this.topTip.show(e, d)
        }
      })
      .on('mouseout', () => {
        this.bottomTip.hide()
        this.topTip.hide()
      })
      .on('click', (event, d: any) => {
        this.topTip.hide()
        this.bottomTip.hide()
        this.smartLinkService.goToLink(d.link)
      })

    // Inner circle text
    slice
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('transform', (d: any) => {
        const middleAngle = (d.startAngle + d.endAngle) / 2 - Math.PI / 2

        const distance =
          this.innerRadius + (this.middleRadius - this.innerRadius) / 2

        var x = distance * Math.cos(middleAngle)
        var y = distance * Math.sin(middleAngle)

        return `translate(${x} ${y})`
      })
      .text((d: any) => {
        // We don't display labels if the angle is too thin
        if (d.endAngle - d.startAngle >= this.minAngleToDisplayLabelInside) {
          // Get the doughnut width to truncate text
          const width = this.middleRadius - this.innerRadius
          if (d.label.length > width / 6) {
            return d.label.substring(0, width / 6 - 3) + '...'
          }
          return d.label
        }
      })
      .style('font-size', '1.1rem')
      .on('mouseover', (e: any, d: any) => {
        centralText.text(`${d.count} incentive${d.count > 1 ? 's' : ''}`)
        const angle = ((d.startAngle + d.endAngle) / 2 / this.tau) * 360
        if (angle > 90 && angle <= 270) {
          this.bottomTip.show(e, d)
        } else {
          this.topTip.show(e, d)
        }
      })
      .on('mouseout', (d: any) => {
        centralText.text('')
        this.bottomTip.hide()
        this.topTip.hide()
      })
      .on('click', (event, d: any) => {
        this.topTip.hide()
        this.bottomTip.hide()
        this.smartLinkService.goToLink(d.link)
      })

    // Outer circle
    const outerSlice = g
      .selectAll('.outer-slice')
      .data(subThemes)
      .enter()
      .append('g')
      .attr('class', 'outer-slice pointer')

    outerSlice
      .append('path')
      .datum((d: any) => this.getAngles(d, subThemes.length))
      .style('fill', (d: any) =>
        d3
          .color(colors[d.parentIndex])
          .brighter((d.indexInParent + 2) / 6)
          .toString()
      )
      .style('stroke', '#fff')
      .attr('d', outerArc as any)
      .on('mouseover', (e: any, d: any) => {
        centralText.text(`${d.count} incentive${d.count > 1 ? 's' : ''}`)
        const angle = ((d.startAngle + d.endAngle) / 2 / this.tau) * 360
        if (angle > 90 && angle <= 270) {
          this.bottomTip.show(e, d)
        } else {
          this.topTip.show(e, d)
        }
      })
      .on('mouseout', (d: any) => {
        centralText.text('')
        this.topTip.hide()
        this.bottomTip.hide()
      })
      .on('click', (event, d: any) => {
        this.topTip.hide()
        this.bottomTip.hide()
        this.smartLinkService.goToLink(d.link)
      })

    // Outer slice text
    outerSlice
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('transform', (d: any) => {
        const middleAngle = (d.startAngle + d.endAngle) / 2 - Math.PI / 2

        let distance
        if (d.endAngle - d.startAngle <= this.minAngleToDisplayLabelInside) {
          distance = this.outerRadius + this.margin.top
        } else {
          distance =
            this.middleRadius + (this.outerRadius - this.middleRadius) / 2
        }

        var x = distance * Math.cos(middleAngle)
        var y = distance * Math.sin(middleAngle)

        return `translate(${x} ${y})`
      })
      .text((d: any, i) => {
        // Get the doughnut width to truncate text
        const width = this.outerRadius - this.middleRadius
        if (d.label.length > width / 6) {
          return d.label.substring(0, width / 6 - 3) + '...'
        }
        return d.label
      })
      .style('font-size', '1.1rem')
      .on('mouseover', (e: any, d: any) => {
        centralText.text(`${d.count} incentive${d.count > 1 ? 's' : ''}`)
        const angle = ((d.startAngle + d.endAngle) / 2 / this.tau) * 360
        if (angle > 90 && angle <= 270) {
          this.bottomTip.show(e, d)
        } else {
          this.topTip.show(e, d)
        }
      })
      .on('click', (event, d: any) => {
        this.topTip.hide()
        this.bottomTip.hide()
        this.smartLinkService.goToLink(d.link)
      })

    // Central text.
    const centralText = g
      .append('svg:text')
      .attr('class', 'score')
      .attr('dy', '.80em')
      .attr('text-anchor', 'middle')
      .text('')
  }

  getAngles(d: any, subThemesLength: any): object {
    // Restart if we made a full turn of the circle
    if (this.endAngle >= this.tau) {
      this.endAngle = this.endAngle - this.tau
    }
    this.startAngle = this.endAngle ? this.endAngle : 0

    // Adapt radius to count.
    this.endAngle = this.startAngle + (d.count / this.totalCount) * this.tau

    d.startAngle = this.startAngle
    d.endAngle = this.endAngle
    return d
  }
}
